import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
} from 'react';
import { useNavigate } from 'react-router';
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { AgGridReact, type CustomCellRendererProps } from 'ag-grid-react';

import {
  IconAlertTriangle,
  IconArrowLeft,
  IconDownload,
  IconLoader2,
  IconPencil,
  IconSearch,
  IconX,
} from '@tabler/icons-react';

import {
  ButtonField,
  ButtonFieldIcon,
  ButtonGroup,
  ButtonGroupItem,
  CheckboxField,
  CheckboxGroupField,
  Iconsvg,
  InputField,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Pagination,
  PaginationBar,
  PaginationBarLeft,
  PaginationBarRight,
  PillSuggestion,
  SelectField,
  Tag,
} from '@wk/components-react16';

import useToast from '@/shared/stores/toastStore';
import {
  assignRole,
  getAssignableRoles,
  getRoleHolders,
  revokeRole,
  searchEntraUsers,
} from '@/domains/access/userAccessApi';
import type {
  AssignRoleRequest,
  EntraUser,
  RoleAssignment,
  RoleHolder,
} from '@/types';
import MultiSelect from '@/shared/ui/MultiSelect';
import { downloadCsv } from '@/lib/csv';
import ApplicationsTab from './ApplicationsTab';
import RolesTab from './RolesTab';
import ViewsTab from './ViewsTab';

// Pastel pill class per role — mirrors the Applications env badges (see .roleBadge in index.css).
const ROLE_BADGE: Record<string, string> = {
  CheckpointManager: 'roleBadge--blue',
  ReleaseNotesManager: 'roleBadge--green',
  OrrBackgroundJobsManager: 'roleBadge--gray',
  DRView: 'roleBadge--orange',
  NetworkView: 'roleBadge--slate',
  NOVAView: 'roleBadge--red',
  UserAccessManager: 'roleBadge--gray',
  JiraBoardManager: 'roleBadge--blue',
};

const PAGE_SIZES = [30, 50, 100, 200];

// Same rule the pipeline enforces (Assign-AppRole.ps1): only @wolterskluwer.com.
const WK_EMAIL_RE = /^[\w.-]+@wolterskluwer\.com$/i;

const isGuestUpn = (upn?: string | null) =>
  (upn ?? '').toUpperCase().includes('#EXT#');

const apiErrorMessage = (error: unknown, fallback: string) => {
  const data = (error as AxiosError | undefined)?.response?.data as
    | { title?: string; message?: string }
    | undefined;
  return data?.title || data?.message || fallback;
};

// 403 from the data endpoints → show an explicit Access Denied panel (not a blank page).
const isForbidden = (error: unknown) =>
  (error as AxiosError | undefined)?.response?.status === 403;

// The API returns 409 Conflict ("The user already has this role.") when the role is
// already assigned — treat that as "already had", not a failure.
const isAlreadyAssignedError = (error: unknown) =>
  (error as AxiosError | undefined)?.response?.status === 409 ||
  /already/i.test(apiErrorMessage(error, ''));

// Parse an emails .txt in the exact format the pegasus-integrations pipeline uses:
// one email per line, blank lines ignored, full-line comments (# // ;) ignored,
// inline comments (whitespace + # // ;) stripped, lowercased + de-duped.
const parseEmailsFile = (text: string): { valid: string[]; invalid: string[] } => {
  const valid: string[] = [];
  const invalid: string[] = [];
  const seen = new Set<string>();

  for (const rawLine of text.split(/\r?\n/)) {
    let line = rawLine.trim();
    if (!line) continue;
    if (line.startsWith('#') || line.startsWith('//') || line.startsWith(';')) {
      continue;
    }
    line = line.split(/\s+#/)[0];
    line = line.split(/\s+\/\//)[0];
    line = line.split(/\s+;/)[0];
    line = line.trim();
    if (!line) continue;

    const email = line.toLowerCase();
    if (WK_EMAIL_RE.test(email)) {
      if (!seen.has(email)) {
        seen.add(email);
        valid.push(email);
      }
    } else {
      invalid.push(line);
    }
  }

  return { valid, invalid };
};

type BatchCategory = 'assigned' | 'exists' | 'notfound' | 'error';
type BatchRow = { email: string; category: BatchCategory; detail: string };

const BATCH_COLOR: Record<BatchCategory, string> = {
  assigned: 'text-green-600',
  exists: 'text-gray-500',
  notfound: 'text-orange-600',
  error: 'text-red-600',
};

const UserManagement = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  // Users / Applications (SPN) / Roles / Views tabs.
  const [activeTab, setActiveTab] = useState<
    'users' | 'applications' | 'roles' | 'views'
  >('users');

  // Filter for the list below (client-side).
  const [filterTerm, setFilterTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState<string[]>([]);

  // Add-user popup: directory search + role assignment.
  const [addUserOpen, setAddUserOpen] = useState(false);
  const [term, setTerm] = useState('');
  const [debounced, setDebounced] = useState('');
  const [assignTarget, setAssignTarget] = useState<EntraUser | null>(null);
  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);

  // Bulk upload (same file format as the pipeline) -> batch assign.
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [batchEmails, setBatchEmails] = useState<string[] | null>(null);
  const [batchInvalid, setBatchInvalid] = useState<string[]>([]);
  const [batchFileName, setBatchFileName] = useState('');
  const [batchResults, setBatchResults] = useState<BatchRow[] | null>(null);
  const [batchProgress, setBatchProgress] = useState<{
    done: number;
    total: number;
  } | null>(null);

  const [revokeTarget, setRevokeTarget] = useState<{
    holder: RoleHolder;
    assignment: RoleAssignment;
  } | null>(null);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(50);

  // Below the `sm` breakpoint AG Grid has no height to fill, so we render a card list instead.
  const [isMobileViewport, setIsMobileViewport] = useState(() =>
    typeof window === 'undefined'
      ? false
      : window.matchMedia('(max-width: 640px)').matches,
  );

  // Debounce the directory search (~350ms).
  useEffect(() => {
    const handle = setTimeout(() => setDebounced(term.trim()), 350);
    return () => clearTimeout(handle);
  }, [term]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mediaQuery = window.matchMedia('(max-width: 640px)');
    const onChange = (event: MediaQueryListEvent) =>
      setIsMobileViewport(event.matches);
    setIsMobileViewport(mediaQuery.matches);
    mediaQuery.addEventListener('change', onChange);
    return () => mediaQuery.removeEventListener('change', onChange);
  }, []);

  const holdersQ = useQuery({
    queryKey: ['user-access', 'holders'],
    queryFn: async () => (await getRoleHolders()).data,
  });

  const rolesQ = useQuery({
    queryKey: ['user-access', 'roles'],
    queryFn: async () => (await getAssignableRoles()).data,
    staleTime: 60 * 60 * 1000,
  });

  const searchQ = useQuery({
    queryKey: ['user-access', 'search', debounced],
    queryFn: async () => (await searchEntraUsers(debounced)).data,
    enabled: addUserOpen && !assignTarget && batchEmails === null && debounced.length >= 2,
    placeholderData: keepPreviousData,
  });

  const sortedRoles = useMemo(
    () =>
      [...(rolesQ.data ?? [])].sort((a, b) =>
        a.displayName.localeCompare(b.displayName),
      ),
    [rolesQ.data],
  );

  const roleLabel = (value: string) =>
    rolesQ.data?.find((r) => r.value === value)?.displayName ?? value;

  const roleOptions = useMemo(
    () => sortedRoles.map((r) => ({ value: r.value, label: r.displayName })),
    [sortedRoles],
  );

  const tabClass = (tab: 'users' | 'applications' | 'roles' | 'views') =>
    `px-3 py-2 text-sm font-medium border-b-2 -mb-px cursor-pointer ${
      activeTab === tab
        ? 'border-[#005b92] text-[#005b92]'
        : 'border-transparent text-gray-500 hover:text-gray-800'
    }`;

  const clearBatch = () => {
    setBatchEmails(null);
    setBatchInvalid([]);
    setBatchFileName('');
    setBatchResults(null);
    setBatchProgress(null);
    setSelectedRoles([]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const closeAddUser = () => {
    setAddUserOpen(false);
    setAssignTarget(null);
    setSelectedRoles([]);
    setTerm('');
    setBatchEmails(null);
    setBatchInvalid([]);
    setBatchFileName('');
    setBatchResults(null);
    setBatchProgress(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const toggleRole = (value: string) =>
    setSelectedRoles((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value],
    );

  // Single-user apply: grant newly-checked roles and revoke newly-unchecked ones.
  const applyMutation = useMutation({
    mutationFn: async ({
      principalId,
      principalType,
      toAdd,
      toRemove,
    }: {
      principalId: string;
      principalType: AssignRoleRequest['principalType'];
      userName: string;
      toAdd: string[];
      toRemove: { assignmentId: string; roleValue: string }[];
    }) => {
      const added: string[] = [];
      const removed: string[] = [];
      const failed: string[] = [];
      for (const roleValue of toAdd) {
        try {
          await assignRole({ principalId, roleValue, principalType });
          added.push(roleValue);
        } catch (error) {
          // Already-assigned is not a failure (the role IS present).
          if (isAlreadyAssignedError(error)) added.push(roleValue);
          else failed.push(roleLabel(roleValue));
        }
      }
      for (const item of toRemove) {
        try {
          await revokeRole(item.assignmentId, item.roleValue);
          removed.push(item.roleValue);
        } catch {
          failed.push(roleLabel(item.roleValue));
        }
      }
      return { added, removed, failed };
    },
    onSuccess: ({ added, removed, failed }, variables) => {
      // Issue-002: Entra appRoleAssignments propagate with a lag, so an immediate refetch
      // returns stale data ("changes only visible after refresh"). Patch the cache
      // optimistically for instant feedback, then reconcile real ids once Graph catches up.
      queryClient.setQueryData<RoleHolder[]>(
        ['user-access', 'holders'],
        (prev) => {
          if (!prev) return prev;
          const mkAssignment = (roleValue: string): RoleAssignment => ({
            assignmentId: `optimistic-${variables.principalId}-${roleValue}`,
            appRoleId: rolesQ.data?.find((r) => r.value === roleValue)?.id ?? '',
            roleValue,
            roleDisplayName: roleLabel(roleValue),
          });
          const has = prev.some((h) => h.principalId === variables.principalId);
          const next = prev.map((h) =>
            h.principalId === variables.principalId
              ? {
                  ...h,
                  assignments: [
                    ...h.assignments.filter(
                      (a) =>
                        !removed.includes(a.roleValue) &&
                        !added.includes(a.roleValue),
                    ),
                    ...added.map(mkAssignment),
                  ],
                }
              : h,
          );
          if (!has && added.length) {
            next.push({
              principalId: variables.principalId,
              principalDisplayName: variables.userName,
              principalType:
                variables.principalType as RoleHolder['principalType'],
              userPrincipalName: assignTarget?.userPrincipalName ?? null,
              mail: assignTarget?.mail ?? null,
              assignments: added.map(mkAssignment),
            });
          }
          return next.filter((h) => h.assignments.length > 0);
        },
      );
      window.setTimeout(
        () =>
          queryClient.invalidateQueries({ queryKey: ['user-access', 'holders'] }),
        3000,
      );
      const parts = [
        added.length ? `granted ${added.map(roleLabel).join(', ')}` : '',
        removed.length ? `removed ${removed.map(roleLabel).join(', ')}` : '',
      ].filter(Boolean);
      if (failed.length === 0) {
        showToast('success', {
          title: 'Roles updated',
          message: `${variables.userName}: ${parts.join('; ') || 'no changes'}.`,
        });
        closeAddUser();
      } else {
        showToast('error', {
          title: 'Some changes failed',
          message: `${variables.userName}: ${parts.join('; ') || 'no changes'}; failed ${failed.join(', ')}.`,
        });
      }
    },
    onError: (error, variables) => {
      showToast('error', {
        title: 'Could not update roles',
        message: apiErrorMessage(
          error,
          `An error occurred while updating roles for ${variables.userName}.`,
        ),
      });
    },
  });

  // Bulk assign: resolve each email to a principal, then grant the checked roles.
  const batchMutation = useMutation({
    mutationFn: async ({
      emails,
      roleValues,
    }: {
      emails: string[];
      roleValues: string[];
    }) => {
      const rows: BatchRow[] = [];
      setBatchProgress({ done: 0, total: emails.length });

      for (let i = 0; i < emails.length; i++) {
        const email = emails[i];
        let user: EntraUser | null = null;
        try {
          const found = (await searchEntraUsers(email)).data;
          user =
            found.find(
              (u) =>
                (u.mail ?? '').toLowerCase() === email ||
                (u.userPrincipalName ?? '').toLowerCase() === email,
            ) ?? null;
        } catch {
          user = null;
        }

        if (!user) {
          rows.push({ email, category: 'notfound', detail: 'User not found' });
          setBatchProgress({ done: i + 1, total: emails.length });
          continue;
        }

        let assigned = 0;
        let exists = 0;
        let errored = 0;
        for (const roleValue of roleValues) {
          try {
            await assignRole({
              principalId: user.id,
              roleValue,
              principalType: 'User',
            });
            assigned++;
          } catch (error) {
            if (isAlreadyAssignedError(error)) exists++;
            else errored++;
          }
        }

        const detail =
          [
            assigned ? `${assigned} assigned` : '',
            exists ? `${exists} already had` : '',
            errored ? `${errored} failed` : '',
          ]
            .filter(Boolean)
            .join(', ') || 'No change';
        const category: BatchCategory = errored
          ? 'error'
          : assigned
            ? 'assigned'
            : 'exists';
        rows.push({ email, category, detail });
        setBatchProgress({ done: i + 1, total: emails.length });
      }

      return rows;
    },
    onSuccess: (rows, variables) => {
      setBatchResults(rows);
      setBatchProgress(null);
      queryClient.invalidateQueries({ queryKey: ['user-access', 'holders'] });
      const roles = variables.roleValues.map(roleLabel).join(', ');
      const ok = rows.filter((r) => r.category === 'assigned').length;
      const exists = rows.filter((r) => r.category === 'exists').length;
      const notFound = rows.filter((r) => r.category === 'notfound').length;
      const failed = rows.filter((r) => r.category === 'error').length;
      showToast(failed ? 'error' : 'success', {
        title: 'Bulk assignment complete',
        message: `${roles}: ${ok} updated, ${exists} already had, ${notFound} not found, ${failed} with errors.`,
      });
    },
    onError: (error, variables) => {
      setBatchProgress(null);
      showToast('error', {
        title: 'Bulk assignment failed',
        message: apiErrorMessage(
          error,
          `An error occurred assigning ${variables.roleValues.map(roleLabel).join(', ')}.`,
        ),
      });
    },
  });

  const revokeMutation = useMutation({
    mutationFn: ({
      assignmentId,
      roleValue,
    }: {
      assignmentId: string;
      roleValue: string;
      roleDisplayName: string;
      userName: string;
    }) => revokeRole(assignmentId, roleValue),
    onSuccess: (_data, variables) => {
      showToast('success', {
        title: 'Role revoked',
        message: `${variables.roleDisplayName} removed from ${variables.userName}.`,
      });
      // Optimistic remove (Graph lag) — drop the assignment now; reconcile after.
      queryClient.setQueryData<RoleHolder[]>(['user-access', 'holders'], (prev) =>
        prev
          ?.map((h) => ({
            ...h,
            assignments: h.assignments.filter(
              (a) => a.assignmentId !== variables.assignmentId,
            ),
          }))
          .filter((h) => h.assignments.length > 0),
      );
      window.setTimeout(
        () =>
          queryClient.invalidateQueries({ queryKey: ['user-access', 'holders'] }),
        3000,
      );
      setRevokeTarget(null);
    },
    onError: (error, variables) => {
      showToast('error', {
        title: 'Could not revoke role',
        message: apiErrorMessage(
          error,
          `An error occurred while removing ${variables.roleDisplayName} from ${variables.userName}.`,
        ),
      });
    },
  });

  const holders = holdersQ.data ?? [];
  const searchResults =
    addUserOpen && !assignTarget && batchEmails === null && debounced.length >= 2
      ? searchQ.data ?? []
      : [];

  const filteredHolders = useMemo(() => {
    const needle = filterTerm.trim().toLowerCase();
    return holders.filter((holder) => {
      const matchesText =
        !needle ||
        [
          holder.principalDisplayName,
          holder.mail ?? '',
          holder.userPrincipalName ?? '',
        ].some((field) => field.toLowerCase().includes(needle));
      const matchesRoles =
        roleFilter.length === 0 ||
        holder.assignments.some((a) => roleFilter.includes(a.roleValue));
      return matchesText && matchesRoles;
    });
  }, [holders, filterTerm, roleFilter]);

  const exportUsersCsv = () => {
    const header = ['Name', 'Email', 'Type', 'Roles'];
    const rows = filteredHolders.map((h) => [
      h.principalDisplayName,
      h.mail ?? h.userPrincipalName ?? '',
      h.principalType,
      h.assignments.map((a) => a.roleValue).join('; '),
    ]);
    downloadCsv(`user-access-${new Date().toISOString().slice(0, 10)}`, [
      header,
      ...rows,
    ]);
  };

  const pageCount = Math.max(1, Math.ceil(filteredHolders.length / limit));
  useEffect(() => setPage(1), [filterTerm, roleFilter]);
  useEffect(() => {
    if (page > pageCount) setPage(1);
  }, [page, pageCount]);
  const pagedHolders = filteredHolders.slice((page - 1) * limit, page * limit);

  const assignableValues = useMemo(
    () => new Set((rolesQ.data ?? []).map((r) => r.value)),
    [rolesQ.data],
  );

  // Selected user's current assignable roles + the add/remove diff against the checkbox
  // selection. In single-user mode `selectedRoles` is the *desired* final set.
  const targetHolder = assignTarget
    ? holders.find((h) => h.principalId === assignTarget.id)
    : undefined;
  const heldAssignable = useMemo(
    () =>
      (targetHolder?.assignments ?? []).filter((a) =>
        assignableValues.has(a.roleValue),
      ),
    [targetHolder, assignableValues],
  );
  const rolesToAdd = selectedRoles.filter(
    (v) => !heldAssignable.some((a) => a.roleValue === v),
  );
  const rolesToRemove = heldAssignable.filter(
    (a) => !selectedRoles.includes(a.roleValue),
  );

  const inBatch = batchEmails !== null;
  const busy = applyMutation.isPending || batchMutation.isPending;
  const primaryDisabled =
    busy ||
    (inBatch
      ? !batchEmails?.length || selectedRoles.length === 0
      : !assignTarget || (rolesToAdd.length === 0 && rolesToRemove.length === 0));
  const showPrimary = !(inBatch && batchResults);

  const handleBack = () => navigate('/settings');

  const handleFileSelected = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const text = await file.text();
    const { valid, invalid } = parseEmailsFile(text);
    setAssignTarget(null);
    setTerm('');
    setSelectedRoles([]);
    setBatchResults(null);
    setBatchFileName(file.name);
    setBatchEmails(valid);
    setBatchInvalid(invalid);
    // Allow re-selecting the same file later.
    event.target.value = '';
  };

  // Select a directory user (search result): seed the role checkboxes with any roles
  // they already hold so the editor can add or remove.
  const selectUser = (user: EntraUser) => {
    const holder = holders.find((h) => h.principalId === user.id);
    setSelectedRoles(
      (holder?.assignments ?? [])
        .map((a) => a.roleValue)
        .filter((v) => assignableValues.has(v)),
    );
    setAssignTarget(user);
  };

  // Open the Roles modal pre-loaded with an existing holder (Actions > edit).
  const editHolder = (holder: RoleHolder) => {
    selectUser({
      id: holder.principalId,
      displayName: holder.principalDisplayName,
      mail: holder.mail,
      userPrincipalName: holder.userPrincipalName ?? '',
      isGuest: isGuestUpn(holder.userPrincipalName),
    });
    setAddUserOpen(true);
  };

  const handlePrimary = () => {
    if (inBatch) {
      if (batchEmails) {
        batchMutation.mutate({ emails: batchEmails, roleValues: selectedRoles });
      }
    } else if (assignTarget) {
      applyMutation.mutate({
        principalId: assignTarget.id,
        principalType: 'User',
        userName: assignTarget.displayName,
        toAdd: rolesToAdd,
        toRemove: rolesToRemove.map((a) => ({
          assignmentId: a.assignmentId,
          roleValue: a.roleValue,
        })),
      });
    }
  };

  const confirmRevoke = () => {
    if (!revokeTarget) return;
    revokeMutation.mutate({
      assignmentId: revokeTarget.assignment.assignmentId,
      roleValue: revokeTarget.assignment.roleValue,
      roleDisplayName: revokeTarget.assignment.roleDisplayName,
      userName: revokeTarget.holder.principalDisplayName,
    });
  };

  // Name + Guest tag + email subtitle — shared by the holders grid, search grid, and mobile cards.
  const renderUserIdentity = (
    name: string,
    email: string | null | undefined,
    isGuest: boolean,
    nameClassName = 'font-medium',
  ) => (
    <>
      <span className="flex items-center gap-2">
        <span className={nameClassName}>{name}</span>
        {isGuest ? <Tag color="orange">Guest</Tag> : null}
      </span>
      <span className="block truncate text-xs text-gray-500">{email ?? '—'}</span>
    </>
  );

  // Edit-roles pencil, shared by the desktop grid (Actions) and the mobile cards.
  const renderEditButton = (holder: RoleHolder) => (
    <button
      type="button"
      aria-label={`Edit Roles for ${holder.principalDisplayName}`}
      title="Edit Roles"
      onClick={() => editHolder(holder)}
      className="flex h-7 w-7 cursor-pointer items-center justify-center text-gray-500 hover:text-gray-800"
    >
      <IconPencil size={16} />
    </button>
  );

  // Role tags with an inline remove (×) / lock, shared by the desktop grid and mobile cards.
  const renderRoleChips = (holder: RoleHolder) => (
    <div className="flex flex-wrap items-center gap-2">
      {holder.assignments.map((assignment) => (
        <span
          key={assignment.assignmentId}
          className={`roleBadge ${ROLE_BADGE[assignment.roleValue] ?? 'roleBadge--gray'}`}
        >
          <PillSuggestion size="small">
            <span className="inline-flex items-center gap-1.5">
              {assignment.roleDisplayName}
              {/* role="button", not <button>: PillSuggestion already renders a
                  <button>, and <button> inside <button> is invalid HTML. */}
              <span
                role="button"
                tabIndex={0}
                aria-label={`Revoke ${assignment.roleDisplayName} from ${holder.principalDisplayName}`}
                title="Revoke Role"
                onClick={(e) => {
                  e.stopPropagation();
                  setRevokeTarget({ holder, assignment });
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    e.stopPropagation();
                    setRevokeTarget({ holder, assignment });
                  }
                }}
                className="inline-flex cursor-pointer opacity-70 hover:opacity-100"
              >
                <IconX size={12} stroke={2.5} />
              </span>
            </span>
          </PillSuggestion>
        </span>
      ))}
    </div>
  );

  // Ordered checkbox list to add/remove several roles at once. `selectedRoles` is the
  // desired set: pre-seeded with the user's current roles (single-user) or empty (batch).
  const renderRoleCheckboxes = () => (
    <CheckboxGroupField size="small">
      <div className="flex flex-col gap-2">
        {sortedRoles.map((role) => (
          <CheckboxField key={role.value} label={role.displayName}>
            <input
              type="checkbox"
              name={`role-${role.value}`}
              checked={selectedRoles.includes(role.value)}
              disabled={busy}
              onChange={() => toggleRole(role.value)}
            />
          </CheckboxField>
        ))}
      </div>
    </CheckboxGroupField>
  );

  return (
    <div className="flex flex-col gap-4 px-4 lg:pr-8 py-4 h-full max-sm:h-auto max-sm:gap-3 max-sm:px-0 max-sm:py-0">
      <div className="flex items-center">
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <ButtonFieldIcon>
            <button onClick={handleBack} aria-label="Back to Settings">
              <IconArrowLeft size={28} />
            </button>
          </ButtonFieldIcon>
          User Management
        </h1>
      </div>
      <p className="text-sm text-gray-500 -mt-2">
        Review who has access to Conformance and assign or revoke Entra roles.
        Roles also control which evaluation views (DR / Network / NOVA) and
        checkpoint sets a user can see.
      </p>

      <div className="flex gap-2 border-b border-gray-200">
        <button
          type="button"
          onClick={() => setActiveTab('users')}
          className={tabClass('users')}
        >
          Users
        </button>
        <button
          type="button"
          onClick={() => setActiveTab('applications')}
          className={tabClass('applications')}
        >
          Applications
        </button>
        <button
          type="button"
          onClick={() => setActiveTab('roles')}
          className={tabClass('roles')}
        >
          Roles
        </button>
        <button
          type="button"
          onClick={() => setActiveTab('views')}
          className={tabClass('views')}
        >
          Views
        </button>
      </div>

      {activeTab === 'applications' ? (
        <ApplicationsTab />
      ) : activeTab === 'roles' ? (
        <RolesTab />
      ) : activeTab === 'views' ? (
        <ViewsTab />
      ) : holdersQ.isError ? (
        <div className="flex flex-1 flex-col items-center justify-center gap-2 rounded-md border bg-white p-8 text-center">
          <IconAlertTriangle size={28} className="text-amber-500" />
          <p className="text-base font-semibold text-gray-800">
            {isForbidden(holdersQ.error)
              ? 'Access denied'
              : 'Could not load users'}
          </p>
          <p className="max-w-md text-sm text-gray-500">
            {isForbidden(holdersQ.error)
              ? 'You need the User Access Manager role to view and manage Conformance access.'
              : apiErrorMessage(
                  holdersQ.error,
                  'An error occurred while loading users. Please try again.',
                )}
          </p>
        </div>
      ) : (
        <>
      <div className="flex flex-wrap items-end gap-3">
        {/* Styled to match the Applications filter on the Evaluations screen exactly:
            cg-field label + the MultiSelect (small) box. Kept as a free-text filter. */}
        <div className="cg-field flex min-w-0 flex-1 flex-col sm:max-w-xs">
          <label className="cg-field-label" htmlFor="user-filter">
            Filter
          </label>
          <div className="group border border-input text-sm hover:border-[#005b92] hover:shadow-[0_0_0_.0625rem_#005b92] focus-within:border-[#000] focus-within:shadow-[0_0_0_.0625rem_#000] focus-within:hover:border-[#000] focus-within:hover:shadow-[0_0_0_.0625rem_#000]">
            <div className="flex min-h-8 items-center gap-1 px-[15px] py-1">
              <input
                id="user-filter"
                type="text"
                name="filter"
                value={filterTerm}
                onChange={(event) => setFilterTerm(event.target.value)}
                placeholder="Filter by name or email"
                autoComplete="off"
                className="ml-2 flex-1 bg-transparent outline-none placeholder:text-muted-foreground"
              />
            </div>
          </div>
        </div>
        <div className="cg-field flex w-full flex-col sm:w-64">
          <label className="cg-field-label" htmlFor="user-role-filter">
            Roles
          </label>
          <MultiSelect
            size="small"
            options={roleOptions}
            value={roleFilter}
            onChange={setRoleFilter}
            placeholder="Filter by role"
          />
        </div>
        <div className="flex shrink-0 items-center gap-2 sm:ml-6">
          <ButtonField mode="text">
            <button
              type="button"
              aria-label="Add User"
              onClick={() => setAddUserOpen(true)}
            >
              <span className="flex items-center gap-2">
                <Iconsvg name="plus" />
                <span className="max-sm:hidden">Add User</span>
              </span>
            </button>
          </ButtonField>
          <ButtonField mode="text">
            <button
              type="button"
              onClick={exportUsersCsv}
              aria-label="Export CSV"
              disabled={filteredHolders.length === 0}
              className="disabled:cursor-not-allowed disabled:opacity-40"
            >
              <span className="flex items-center gap-2">
                <IconDownload size={18} />
                <span className="max-sm:hidden">Export CSV</span>
              </span>
            </button>
          </ButtonField>
        </div>
      </div>

      <div className="flex min-h-0 flex-1 flex-col gap-2">
        <div className="flex items-center justify-between gap-2">
          <h2 className="text-lg font-semibold">Current access</h2>
        </div>

        {isMobileViewport ? (
          <div className="flex flex-col gap-3">
            {holdersQ.isLoading ? (
              <div className="flex min-h-[160px] items-center justify-center text-gray-500">
                <IconLoader2 size={22} className="animate-spin" />
              </div>
            ) : pagedHolders.length === 0 ? (
              <div className="rounded-md border bg-white p-4 text-sm text-gray-600">
                No users found.
              </div>
            ) : (
              pagedHolders.map((holder) => (
                <article
                  key={holder.principalId}
                  className="rounded-lg border bg-white p-3 shadow-sm"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      {renderUserIdentity(
                        holder.principalDisplayName,
                        holder.mail ?? holder.userPrincipalName,
                        isGuestUpn(holder.userPrincipalName),
                        'break-words text-base font-semibold leading-5',
                      )}
                    </div>
                    <div className="flex shrink-0 items-center gap-2">
                      <span className="text-xs text-gray-500">
                        {holder.principalType}
                      </span>
                      {renderEditButton(holder)}
                    </div>
                  </div>
                  <div className="mt-3">
                    <p className="mb-1 text-[10px] uppercase tracking-wide text-gray-500">
                      Roles
                    </p>
                    {renderRoleChips(holder)}
                  </div>
                </article>
              ))
            )}
          </div>
        ) : (
          <div className="cg-ag-grid-field ag-theme-balham min-h-0 flex-1">
            <AgGridReact
              animateRows={false}
              loading={holdersQ.isLoading}
              rowData={pagedHolders}
              columnDefs={[
                {
                  headerName: 'User',
                  field: 'principalDisplayName',
                  flex: 2,
                  minWidth: 280,
                  autoHeight: true,
                  cellRenderer: (params: CustomCellRendererProps<RoleHolder>) => {
                    const holder = params.data;
                    if (!holder) return null;
                    return (
                      <div className="flex flex-col py-1 leading-tight">
                        {renderUserIdentity(
                          holder.principalDisplayName,
                          holder.mail ?? holder.userPrincipalName,
                          isGuestUpn(holder.userPrincipalName),
                        )}
                      </div>
                    );
                  },
                },
                {
                  headerName: 'Type',
                  field: 'principalType',
                  width: 160,
                },
                {
                  headerName: 'Roles',
                  field: 'assignments',
                  flex: 3,
                  minWidth: 320,
                  sortable: false,
                  autoHeight: true,
                  cellRenderer: (params: CustomCellRendererProps<RoleHolder>) => {
                    const holder = params.data;
                    if (!holder) return null;
                    return <div className="py-2">{renderRoleChips(holder)}</div>;
                  },
                },
                {
                  headerName: 'Actions',
                  colId: 'actions',
                  width: 88,
                  sortable: false,
                  cellRenderer: (params: CustomCellRendererProps<RoleHolder>) => {
                    const holder = params.data;
                    if (!holder) return null;
                    return (
                      <div className="flex h-full items-center">
                        {renderEditButton(holder)}
                      </div>
                    );
                  },
                },
              ]}
            />
          </div>
        )}

        <PaginationBar>
          <PaginationBarLeft>{filteredHolders.length} results</PaginationBarLeft>
          <PaginationBarRight>
            <div className="flex items-center gap-4">
              <p className="text-sm text-muted-foreground">
                Page {page} of {pageCount}
              </p>
              <SelectField size="small">
                <select
                  value={limit}
                  onChange={(event: ChangeEvent<HTMLSelectElement>) => {
                    setLimit(Number(event.target.value));
                    setPage(1);
                  }}
                >
                  {PAGE_SIZES.map((size) => (
                    <option key={size} value={size}>
                      {size}
                    </option>
                  ))}
                </select>
              </SelectField>
              <Pagination
                pageIndex={page}
                pageCount={pageCount}
                onUserRequest={(next: number) => setPage(next)}
              />
            </div>
          </PaginationBarRight>
        </PaginationBar>
      </div>
        </>
      )}

      {/* Add user: search a single user OR upload an emails file for bulk assignment.
          Roles are picked as a multi-select checkbox list (add several at once). */}
      <Modal
        isOpen={addUserOpen}
        mobileMode="fullscreen"
        srLabelledBy="add-user-title"
        srDescribedBy="add-user-desc"
        onUserRequest={closeAddUser}
      >
        <ModalHeader titleId="add-user-title" onUserRequest={closeAddUser}>
          Roles
        </ModalHeader>
        <ModalBody descriptionId="add-user-desc">
          {inBatch ? (
            <>
              <div className="mb-3 flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="flex items-center gap-2 font-medium">
                    <Iconsvg name="upload-line" />
                    <span className="truncate">{batchFileName}</span>
                  </p>
                  <p className="text-xs text-gray-500">
                    {batchEmails?.length ?? 0} valid email(s)
                    {batchInvalid.length ? `, ${batchInvalid.length} ignored` : ''}
                  </p>
                </div>
                <ButtonField mode="text">
                  <button type="button" onClick={clearBatch} disabled={busy}>
                    Clear File
                  </button>
                </ButtonField>
              </div>

              {batchEmails?.length ? (
                <div className="mb-3 max-h-32 overflow-auto rounded-md border bg-gray-50 p-2 text-xs text-gray-600">
                  {batchEmails.map((email) => (
                    <div key={email} className="truncate">
                      {email}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="mb-3 text-sm text-red-600">
                  No valid @wolterskluwer.com emails found in this file.
                </p>
              )}

              {batchInvalid.length ? (
                <details className="mb-3 text-xs text-gray-500">
                  <summary className="cursor-pointer">
                    {batchInvalid.length} ignored line(s)
                  </summary>
                  <div className="mt-1 max-h-24 overflow-auto rounded border bg-gray-50 p-2">
                    {batchInvalid.map((line, index) => (
                      <div key={`${line}-${index}`} className="truncate">
                        {line}
                      </div>
                    ))}
                  </div>
                </details>
              ) : null}

              <p className="mb-1 text-xs uppercase tracking-wide text-gray-500">
                Roles to assign
              </p>
              {renderRoleCheckboxes()}

              {batchProgress ? (
                <div className="mt-3 flex items-center gap-2 text-sm text-gray-600">
                  <IconLoader2 size={16} className="animate-spin" />
                  Processing {batchProgress.done}/{batchProgress.total}…
                </div>
              ) : null}

              {batchResults ? (
                <div className="mt-3">
                  <p className="mb-1 text-xs uppercase tracking-wide text-gray-500">
                    Results
                  </p>
                  <div className="max-h-48 divide-y overflow-auto rounded-md border text-sm">
                    {batchResults.map((row) => (
                      <div
                        key={row.email}
                        className="flex items-center justify-between gap-2 px-3 py-1.5"
                      >
                        <span className="truncate">{row.email}</span>
                        <span className={`shrink-0 ${BATCH_COLOR[row.category]}`}>
                          {row.detail}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}
            </>
          ) : !assignTarget ? (
            <>
              <div className="flex items-end gap-2">
                <div className="flex-1">
                  <InputField label="Search the directory by name or email">
                    <input
                      type="text"
                      name="user-search"
                      placeholder="Start typing at least 2 characters…"
                      value={term}
                      onChange={(event) => setTerm(event.target.value)}
                      autoComplete="off"
                    />
                  </InputField>
                </div>
                <ButtonFieldIcon>
                  <button
                    type="button"
                    aria-label="Upload an Emails File for Bulk Assignment"
                    title="Upload .txt (Bulk Assign)"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <Iconsvg name="upload-line" />
                  </button>
                </ButtonFieldIcon>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".txt,text/plain"
                  className="hidden"
                  onChange={handleFileSelected}
                />
              </div>
              <p className="mt-1 text-xs text-gray-500">
                Or upload a <code>.txt</code> (one email per line, only
                @wolterskluwer.com; lines starting with # // ; are ignored) to
                assign roles to many users at once.
              </p>

              {debounced.length >= 2 ? (
                searchQ.isFetching && !searchResults.length ? (
                  <div className="mt-2 flex items-center gap-2 rounded-md border px-3 py-3 text-sm text-gray-500">
                    <IconLoader2 size={16} className="animate-spin" /> Searching…
                  </div>
                ) : searchResults.length === 0 ? (
                  <div className="mt-2 flex items-center gap-2 rounded-md border px-3 py-3 text-sm text-gray-500">
                    <IconSearch size={16} /> No users match “{debounced}”.
                  </div>
                ) : (
                  <div className="cg-ag-grid-field ag-theme-balham mt-2 max-h-72 overflow-auto">
                    <AgGridReact
                      domLayout="autoHeight"
                      headerHeight={0}
                      rowHeight={44}
                      rowData={searchResults}
                      rowStyle={{ cursor: 'pointer' }}
                      onRowClicked={(event) =>
                        event.data && selectUser(event.data)
                      }
                      columnDefs={[
                        {
                          headerName: 'User',
                          field: 'displayName',
                          flex: 1,
                          cellRenderer: (
                            params: CustomCellRendererProps<EntraUser>,
                          ) => {
                            const user = params.data;
                            if (!user) return null;
                            return (
                              <div className="flex flex-col py-1 leading-tight">
                                {renderUserIdentity(
                                  user.displayName,
                                  user.mail ?? user.userPrincipalName,
                                  user.isGuest,
                                )}
                              </div>
                            );
                          },
                        },
                      ]}
                    />
                  </div>
                )
              ) : null}
            </>
          ) : (
            <>
              <div className="mb-3 flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="font-medium">{assignTarget.displayName}</p>
                  <p className="truncate text-xs text-gray-500">
                    {assignTarget.mail ?? assignTarget.userPrincipalName}
                  </p>
                </div>
                <ButtonField mode="text">
                  <button
                    type="button"
                    onClick={() => {
                      setAssignTarget(null);
                      setSelectedRoles([]);
                    }}
                  >
                    Change
                  </button>
                </ButtonField>
              </div>

              <p className="mb-1 text-xs uppercase tracking-wide text-gray-500">
                Roles
              </p>
              <p className="mb-2 text-xs text-gray-500">
                Check to grant, uncheck to remove — changes apply when you click
                Apply.
              </p>
              {renderRoleCheckboxes()}
            </>
          )}
        </ModalBody>
        <ModalFooter>
          <ButtonGroup mode="static">
            <ButtonGroupItem slot="buttonGroupItem">
              <ButtonField mode="text">
                <button type="button" onClick={closeAddUser} disabled={busy}>
                  {inBatch && batchResults ? 'Close' : 'Cancel'}
                </button>
              </ButtonField>
            </ButtonGroupItem>
            {showPrimary ? (
              <ButtonGroupItem slot="buttonGroupItem">
                <ButtonField>
                  <button
                    type="button"
                    onClick={handlePrimary}
                    disabled={primaryDisabled}
                  >
                    {inBatch
                      ? `Apply to ${batchEmails?.length ?? 0} User(s)`
                      : 'Apply'}
                    {busy ? (
                      <IconLoader2 size={16} className="animate-spin" />
                    ) : null}
                  </button>
                </ButtonField>
              </ButtonGroupItem>
            ) : null}
          </ButtonGroup>
        </ModalFooter>
      </Modal>

      {/* Revoke confirmation modal */}
      <Modal
        isOpen={revokeTarget !== null}
        mobileMode="fullscreen"
        srLabelledBy="revoke-modal-title"
        srDescribedBy="revoke-modal-description"
        onUserRequest={() => setRevokeTarget(null)}
      >
        <ModalHeader
          titleId="revoke-modal-title"
          onUserRequest={() => setRevokeTarget(null)}
        >
          Revoke Role
        </ModalHeader>
        <ModalBody descriptionId="revoke-modal-description">
          <div className="flex items-start gap-2 text-sm text-gray-700">
            <IconAlertTriangle size={20} className="mt-0.5 shrink-0 text-red-500" />
            <span>
              Remove{' '}
              <span className="font-semibold">
                {revokeTarget?.assignment.roleDisplayName}
              </span>{' '}
              from{' '}
              <span className="font-semibold">
                {revokeTarget?.holder.principalDisplayName}
              </span>
              ? They will lose the access this role grants.
            </span>
          </div>
        </ModalBody>
        <ModalFooter>
          <ButtonGroup mode="static">
            <ButtonGroupItem slot="buttonGroupItem">
              <ButtonField mode="text">
                <button type="button" onClick={() => setRevokeTarget(null)}>
                  Cancel
                </button>
              </ButtonField>
            </ButtonGroupItem>
            <ButtonGroupItem slot="buttonGroupItem">
              <ButtonField>
                <button
                  type="button"
                  onClick={confirmRevoke}
                  disabled={revokeMutation.isPending}
                >
                  Revoke
                  {revokeMutation.isPending ? (
                    <IconLoader2 size={16} className="animate-spin" />
                  ) : null}
                </button>
              </ButtonField>
            </ButtonGroupItem>
          </ButtonGroup>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default UserManagement;
